import {AppBar} from "@progress/kendo-react-layout";
import React, {useContext, useEffect, useRef, useState} from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {StoreContext} from "../../store/StoreContext";
import "./main.scss";
import useMain from "../../hooks/useMain";
import {Button} from "@progress/kendo-react-buttons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCircleQuestion,
  faFileLines,
  faQrcode,
  faRectangleList,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {localSession} from "../../helpers/local-storage";
import {LOCAL_SESSION} from "../../constants/local-entity";
import {Device} from "@capacitor/device";
import {Network} from "@capacitor/network";
import {App as CapApp} from "@capacitor/app";
import Profile from "../../components/profile/Profile";
import ReauthModal from "../../components/reauth-modal/ReauthModal";
import {swalWarningAlert} from "../../alerts/sweetalert";

const MainLayout = () => {
  const navigate = useNavigate();
  const {
    appTitle,
    session,
    setSession,
    online,
    setOnline,
    loading,
    setPlatform,
  } = useContext(StoreContext);
  const {start, syncAllPending} = useMain();
  const {rol} = useParams();
  const location = useLocation();

  const [inHome, setInHome] = useState(false);
  const [show, setShow] = useState(false);
  const [showReauth, setShowReauth] = useState(false);
  const prevOnline = useRef(true);
  const isSyncing = useRef(false);
  const syncAllPendingRef = useRef(syncAllPending);

  useEffect(() => {
    syncAllPendingRef.current = syncAllPending;
  });

  useEffect(() => {
    const sessionAux = localSession.getItem(LOCAL_SESSION);
    setSession(sessionAux);
  }, []);

  // Escuchar evento de token expirado para mostrar modal de re-login
  useEffect(() => {
    const handleTokenExpired = () => {
      if (!showReauth) setShowReauth(true);
    };
    window.addEventListener("auth:token-expired", handleTokenExpired);
    return () => window.removeEventListener("auth:token-expired", handleTokenExpired);
  }, [showReauth]);

  useEffect(() => {
    setInHome(location?.pathname === "/cermaq/");
  }, [location?.pathname]);

  useEffect(() => {
    if (session?.username) start(session.username, session.profile);
  }, [session]);

  const appActiveRef = useRef(true);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (!appActiveRef.current) return; // No hacer polling si la app está en background

      let connected = navigator.onLine;
      try {
        const {platform} = await Device.getInfo();
        setPlatform(platform);
        if (platform !== "web") {
          ({connected} = await Network.getStatus());
        }
      } catch (_err) {
        // En web, Device.getInfo() intenta cargar un chunk JS desde el servidor
        // de desarrollo y falla cuando no hay red. navigator.onLine no tiene
        // esa dependencia y sigue siendo confiable como fallback.
      }

      if (connected && !prevOnline.current && !isSyncing.current) {
        isSyncing.current = true;
        try {
          const synced = await syncAllPendingRef.current();
          if (synced > 0) {
            swalWarningAlert(
              `Se sincronizaron ${synced} inspección(es) pendiente(s). Ya están guardadas en el servidor.`,
              "OK"
            );
          }
        } finally {
          isSyncing.current = false;
        }
      }
      prevOnline.current = connected;
      setOnline(connected);
    }, 2500);

    // Pausar/resumir polling cuando la app entra/sale de background (mobile)
    let appStateListener;
    CapApp.addListener("appStateChange", (state) => {
      appActiveRef.current = state.isActive;
    }).then(listener => { appStateListener = listener; }).catch(() => {});

    return () => {
      clearInterval(interval);
      if (appStateListener) appStateListener.remove();
    };
  }, []);

  const onBack = () => navigate(-1);

  const handleClose = () => setShow(false);

  const handleReauthClose = (success) => {
    setShowReauth(false);
    if (success) {
      swalWarningAlert("Sesion renovada correctamente.", "OK");
    }
  };

  return (
    <div className="layout">
      <AppBar className="app-bar d-block w-100" style={{height: 65}}>
        <div className="row">
          <div className="col-9 pe-0">
            <div className="d-flex align-items-center">
              <Button
                className={`me-3 k-button k-button-md k-rounded-md k-button-flat k-button-flat-base ${
                  inHome ? "d-none" : ""
                }`}
                onClick={() => onBack()}
              >
                <span className="fs-3 k-icon k-i-arrow-left" />
              </Button>
              <h1 className="title text-truncate w-100 me-2">
              {rol === "Inspector" ? "SSO - " : rol === "Jefatura" ? "Supervisor - " : rol ? `${rol} - ` : ""}
              {appTitle}
              </h1>
            </div>
          </div>
          <div className="col-3 ps-0">
            <div className="w-100 h-100 d-flex align-items-center">
              {online ? (
                <span className="badge rounded-pill bg-success px-3 ms-auto">
                  On line
                </span>
              ) : (
                <span className="badge rounded-pill bg-danger px-3 ms-auto">
                  Off line
                </span>
              )}
            </div>
          </div>
        </div>
      </AppBar>

      <div style={{height: "calc(100% - 65px - 80px)", overflowY: "auto"}}>

        {loading && (
          <div className="w-100 d-flex py-3 justify-content-center align-items-center">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-3">Cargando...</span>
          </div>
        )}
        <div className="container py-3 h-100">
          <Outlet />
        </div>
      </div>

      <nav
        className="navbar d-flex align-items-center bg-primary w-100"
        style={{height: 80}}
      >
        <div
          style={{width: "20%"}}
          className={`d-flex justify-content-center px-3 ${
            inHome ? "d-none" : "d-flex"
          }`}
        >
          <NavLink
            className={({isActive}) =>
              `button-hidden text-decoration-none px-3 py-2 d-flex flex-column align-items-center text-white ${
                inHome ? "d-none" : ""
              } ${isActive ? "activeMenu" : ""}`
            }
            to="/cermaq/index"
          >
            <FontAwesomeIcon className="fs-4" icon={faFileLines} />
            <span className="d-none d-md-block fs-6">Inspecciones</span>
          </NavLink>
        </div>

        <div
          style={{width: "20%"}}
          className={`d-flex justify-content-center px-3 ${
            inHome ? "d-none" : "d-flex"
          }`}
        >
          <NavLink
            end={true}
            className={({isActive}) =>
              `button-hidden text-decoration-none px-3 py-2 d-flex flex-column align-items-center text-white ${
                inHome ? "d-none" : ""
              } ${isActive ? "activeMenu" : ""}`
            }
            to={`/cermaq/my-inspections/${rol || ""}`}
          >
            <FontAwesomeIcon className="fs-4" icon={faRectangleList} />
            <span className="d-none d-md-block fs-6">Mis Inspecciones</span>
          </NavLink>
        </div>

        <div
          style={{width: "20%"}}
          className={`d-flex justify-content-center px-3 ${
            inHome ? "d-none" : "d-flex"
          }`}
        >
          <NavLink
            className={({isActive}) =>
              `button-hidden text-decoration-none px-3 py-2 d-flex flex-column align-items-center text-white ${
                inHome ? "d-none" : ""
              } ${isActive ? "activeMenu" : ""}`
            }
            to={`/cermaq/qr-reader`}
          >
            <FontAwesomeIcon className="fs-4" icon={faQrcode} />
            <span className="d-none d-md-block fs-6">Lector QR</span>
          </NavLink>
        </div>

        <div
          style={{width: "20%"}}
          className={`d-flex justify-content-center px-3 ${
            inHome ? "d-none" : "d-flex"
          } `}
        >
          <NavLink
            className={({isActive}) =>
              `button-hidden text-decoration-none px-3 py-2 d-flex flex-column align-items-center text-white ${
                inHome ? "d-none" : ""
              } ${isActive ? "activeMenu" : ""}`
            }
            to={`/cermaq/help`}
          >
            <FontAwesomeIcon className="fs-4" icon={faCircleQuestion} />
            <span className="d-none d-md-block fs-6">Ayuda</span>
          </NavLink>
        </div>

        <div
          style={{width: "20%"}}
          className="d-flex justify-content-center px-3 ms-auto"
        >
          <button
            type="button"
            className="button-hidden rounded-circle bg-white text-muted"
            style={{padding: ".45rem", width: 38, height: 38}}
            onClick={() => setShow(true)}
          >
            <FontAwesomeIcon icon={faUser} />
          </button>
        </div>
      </nav>

      <Profile show={show} close={handleClose} />
      <ReauthModal show={showReauth} onClose={handleReauthClose} />
    </div>
  );
};

export default MainLayout;
